// src/services/feeService.js
// 78차 62차: plan/planDiscountRate/feeType 등을 branches[i] 단위로 오버라이드 가능하게
// 확장(getBranchPlan/getEffectivePlan 신규, calcStoreFee/getPlanFee가 branchIdx 인자
// 지원) — 지점마다 계약 조건이 다른데 store 최상위 필드 하나만 봐서 "지점이 몇 곳이든
// 항상 같은 정산액"이 나오던 버그 수정.
// 68차 신규: 수수료/정산 금액 계산 단일 소스(Single Source of Truth)
// 76차: getPlanFee/calcTotalOwed 추가 — 플랜 구독료가 정산에 반영 안 되던 버그 수정
// 78차 47차: BASIC "계약일로부터 1년 무료" 자동 정책(날짜 기반)을 폐지하고, 총괄관리자가
// 매장별로 직접 설정하는 플랜 할인율(store.planDiscountRate, 0~100%)로 대체 — 신규 매장은
// 등급별 최초계약 기본값(PLAN_DEFAULT_DISCOUNT_RATE)으로 등록해 두고, 이후 총괄관리자가
// 매장별 재량으로 수동 조정하는 방식(구체적 수치는 의도적으로 여기 안 적음).
// (isBasicFreeYearActive/scheduledBasicFreeYearReminder/scheduledBasicUnpaidCleanup 삭제)
// functions/index.js에 동일 로직(서버 계산)이 있으니 여기를 고치면 getPlanFeeServer도 같이 고칠 것.
import { PLAN_PRICES, PLAN_TIERS, normalizePlan, OPS_ADDON_FEE, deriveLegacyOpsAddon } from '../constants';
//
// ⚠️ 발견한 버그 (67차 인수인계서 "코드 중복 정리" 항목의 실체):
//   - SuperAdminStoresScreen.js FeeTab의 calcFee(): 요율(rate)형 매장은
//     revenue * discountRate / 100 으로 계산 (매장 계약서상 요율 그대로 반영)
//   - SuperAdminStoresScreen.js BillingTab의 calcMonthlyFee(): 요율(rate)형 매장도
//     실제 매출/요율과 무관하게 테마 개수 구간별 고정 금액표(rec)를 사용
//   - StoreAdminScreen.js StatsTab / MonthlyReportTab: fixed(정액)형 매장을 전혀
//     고려하지 않고 항상 revenue * discountRate / 100 으로만 계산
//   → 같은 매장인데 "매장별 수수료" 탭과 "수납 관리" 탭에 표시되는 금액이 다르고,
//     정액제 매장은 매장관리자 화면에 아예 잘못된 정산액이 노출되고 있었음.
//   → 계약서(feeType/discountRate/fixedFee, 모두 stores 문서에 저장된 실제 계약 조건)를
//     기준으로 하는 아래 calcStoreFee()를 유일한 계산식으로 사용하도록 통일.
//
// 78차 62차: plan/planDiscountRate/feeType/discountRate/fixedFee/paymentDay/
// contractStart/contractEnd/isDeleted 전부 지점(branches[i])별로 다르게 설정할 수
// 있어야 한다는 요청 — 지점에 자기 값이 있으면 그걸, 없으면(레거시 단일지점 매장 등)
// store 최상위 값으로 폴백. branchIdx가 null이면("전체" 보기) store 최상위 값을
// 그대로 쓰거나(단일지점) 지점들의 값을 합산/최댓값으로 취급한다.
// 80차 후속: [버그 수정, 재발 방지] 단일지점(≤1) 매장은 지점별 오버라이드 개념 자체가
// 없음(EditStore도 이 경우 지점별 편집 UI를 아예 안 보여줌, plan 변경은 오직 store-level
// "💳 플랜 변경" 모달로만 함) — 그런데 엑셀 일괄등록(BulkStoreImportTab)이 지점마다
// plan/opsAddon 등을 항상 명시적으로 채워 넣어서, 단일지점 매장도 branches[0]에 값이
// 박혀 있는 채로 만들어짐. 이후 store-level 모달로 plan/opsAddon을 바꾸면 store 최상위
// 필드만 갱신되는데, 배지 등 지점 단위 조회(hasOpsAddon(store, 0) 등)는 여전히 그
// branches[0]의 낡은 값을 먼저 보고 있어 화면에 반영이 안 되는 버그가 실제 재현됨
// (opsAddon에서 발견/수정 — 동일 구조라 plan에도 잠재해 있었음). 지점이 1개 이하면
// branches[i]의 값 자체를 무시하고 항상 store 최상위 값만 신뢰하도록 해서, 두 값이
// 애초에 서로 다른 진실을 가질 수 없게 함(다지점 매장은 기존 동작 그대로 유지).
const branchOverride = (store, branchIdx, field) => {
  if ((store?.branches?.length || 0) <= 1) return store?.[field];
  const b = branchIdx != null ? store?.branches?.[branchIdx] : null;
  const v = b ? b[field] : undefined;
  return (v !== undefined && v !== null && v !== '') ? v : store?.[field];
};

// 지점 하나의 plan(레거시 폴백 포함)
export const getBranchPlan = (store, branchIdx = null) =>
  normalizePlan(branchOverride(store, branchIdx, 'plan'));

// "전체" 보기용 유효 plan — 지점마다 다를 수 있어, 탭/기능 게이트가 과소 차단되지
// 않도록 지점들 중 가장 높은 등급을 기준으로 삼는다(단일지점/레거시는 그대로 store.plan).
export const getEffectivePlan = (store, scopeBranchIdx = null) => {
  if (scopeBranchIdx != null) return getBranchPlan(store, scopeBranchIdx);
  const branches = store?.branches || [];
  if (branches.length <= 1) return normalizePlan(store?.plan);
  return branches.reduce((best, _, i) => {
    const p = getBranchPlan(store, i);
    return PLAN_TIERS.indexOf(p) > PLAN_TIERS.indexOf(best) ? p : best;
  }, PLAN_TIERS[0]);
};

// 80차 후속: 매장관리자 기능(예약관리 통계·정산 등) 애드온 — plan과 완전히 독립된
// boolean, branchOverride로 plan/planDiscountRate와 동일하게 지점별 오버라이드 지원.
// store.opsAddon 필드가 아직 없는(마이그레이션 전) 레거시 문서는 구버전 plan 값 기준으로
// 안전하게 유추(deriveLegacyOpsAddon) — 정상적으로 마이그레이션된 매장은 이 폴백을 안 탐.
const getBranchOpsAddon = (store, branchIdx) => {
  const raw = branchOverride(store, branchIdx, 'opsAddon');
  if (typeof raw === 'boolean') return raw;
  const rawPlan = branchOverride(store, branchIdx, 'plan');
  return deriveLegacyOpsAddon(rawPlan);
};

// "전체" 보기용 — 지점 중 하나라도 애드온이 켜져 있으면 해당 탭/카드를 보여준다
// (getEffectivePlan과 동일하게, 과소 차단보다 과다 노출이 안전한 UI 게이트 성격).
export const hasOpsAddon = (store, scopeBranchIdx = null) => {
  if (scopeBranchIdx != null) return getBranchOpsAddon(store, scopeBranchIdx);
  const branches = store?.branches || [];
  if (branches.length <= 1) return getBranchOpsAddon(store, null);
  return branches.some((_, i) => getBranchOpsAddon(store, i));
};

// 매장관리자 기능 애드온 월 요금 — branchIdx를 넘기면 그 지점 하나, 안 넘기면(다지점)
// 애드온이 켜진 지점 수만큼 합산(getPlanFee와 동일한 지점별 합산 관례).
export const getOpsAddonFee = (store, branchIdx = null) => {
  if (branchIdx != null) return getBranchOpsAddon(store, branchIdx) ? OPS_ADDON_FEE : 0;
  const branches = store?.branches || [];
  if (branches.length <= 1) return getBranchOpsAddon(store, null) ? OPS_ADDON_FEE : 0;
  return branches.reduce((sum, _, i) => sum + getOpsAddonFee(store, i), 0);
};

// 매장 계약 조건(feeType)에 따른 수수료 계산 — branchIdx를 넘기면 그 지점의 계약
// 조건(없으면 store 값 폴백)을 사용, revenue/themeCount는 호출부가 이미 해당
// 지점(또는 전체) 기준으로 스코프해 넘긴다.
//   - feeType === 'fixed' : 방(테마) 당 정액 × 테마 개수
//   - 그 외(rate)         : 매출 × 요율(%) — 소수점 이하 절사
// 75차: 데드코드 정리 — calcSettleAmount 삭제(호출부 없음)
export const calcStoreFee = (store, revenue, themeCount = 0, branchIdx = null) => {
  const feeType      = branchOverride(store, branchIdx, 'feeType');
  const discountRate = branchOverride(store, branchIdx, 'discountRate');
  const fixedFee      = branchOverride(store, branchIdx, 'fixedFee');
  if (feeType === 'fixed') {
    return (fixedFee || 0) * themeCount;
  }
  return Math.floor((revenue || 0) * (discountRate || 0) / 100);
};

// 매장의 전체 테마 개수 (branches[].themes[] 합산)
// 78차 46차: 소프트 삭제된 지점/테마(isDeleted:true)는 실제로 더 이상 운영하지 않는
// 것이므로 정액제(feeType:'fixed') 청구 등 테마 개수 기반 계산에서 제외.
export const getStoreThemeCount = (store) =>
  (store?.branches || []).filter(b => b.isDeleted !== true)
    .reduce((a, b) => a + (b.themes || []).filter(t => t.isDeleted !== true).length, 0);

// 78차 47차: 매장별 플랜 할인율(0~100%, 총괄관리자가 EditStore/등록 폼에서 직접 설정) —
// 값이 없는 레거시 매장은 0%(할인 없음, 정가)로 취급.
// 78차 62차: branchIdx 지점별 오버라이드 지원.
export const getPlanDiscountRate = (store, branchIdx = null) =>
  Math.min(100, Math.max(0, Number(branchOverride(store, branchIdx, 'planDiscountRate')) || 0));

// 76차 신규: 매장 plan(구독) 월 정액 요금 — 수수료(calcStoreFee, 매출 연동)와는
// 완전히 별개의 정책이다(docs/PROJECT_OVERVIEW.md: 플랜=기능 이용료 구독제 /
// 수수료=매출 연동 커미션). 78차 47차: 정가에서 planDiscountRate(%)만큼 할인해 청구.
// 78차 62차: branchIdx를 넘기면 그 지점 하나의 구독료, 안 넘기면("전체") 지점이
// 여럿인 매장은 지점별 plan/할인율이 각자 다를 수 있어 전부 합산한 총액을 반환
// (기존엔 store 최상위 값 하나만 봐서 지점이 몇 곳이든 항상 같은 금액이 나오던 버그).
export const getPlanFee = (store, branchIdx = null) => {
  if (branchIdx != null) {
    const base = PLAN_PRICES[getBranchPlan(store, branchIdx)] || 0;
    return Math.round(base * (100 - getPlanDiscountRate(store, branchIdx)) / 100);
  }
  const branches = store?.branches || [];
  if (branches.length <= 1) {
    const base = PLAN_PRICES[normalizePlan(store?.plan)] || 0;
    return Math.round(base * (100 - getPlanDiscountRate(store)) / 100);
  }
  return branches.reduce((sum, _, i) => sum + getPlanFee(store, i), 0);
};

// 78차 47차: "플랜명 (할인률 X%)" 형태로 조합해서 쓰는 화면들을 위한 짧은 라벨 —
// 실제 청구 금액(원)은 호출부에 이미 별도 컬럼/행(월 청구액 등)으로 나오고 있어
// 여기서 중복으로 보여주지 않는다(중첩 괄호로 보기 불편하다는 피드백 반영).
export const getPlanFeeLabel = (store, branchIdx = null) => `할인률 ${getPlanDiscountRate(store, branchIdx)}%`;

// 78차 39차: PICK 테마 무제한 등록 — 지금까지 개수 제한이 없어 "기본 하나만 무료, 나머지는
// 등록해봐야 의미 없다"는 지적에 따라 매장당 무료 슬롯 1개 + 초과분 1개당 월 추가 과금 도입.
// 플랜 구독료와 마찬가지로 매출과 무관한 고정 추가금이라 별도 상수로 분리.
// 서버(functions/index.js)에도 동일한 값이 있으니 바꿀 땐 항상 같이 맞출 것.
// 80차 후속: 요금 재구성과 함께 2,000원→5,000원 인상(사용자 확정) — 대형 매장은 테마(방)
// 수가 많아, 정액 단일가로는 다지점·다테마 매장이 상대적으로 저렴하게 무제한 PICK을
// 가져가던 구조를 보완.
export const PICK_FREE_SLOT_COUNT = 1;
export const PICK_ADDON_FEE = 5000;

// paidPickSlotCount: 무료 슬롯을 초과해 등록된(= events 문서에 isPaidSlot:true로 표시된),
// 현재 활성 상태인 PICK 개수. 호출부(eventService.getActivePickSlotCount)에서 계산해 넘긴다.
export const getPickAddonFee = (paidPickSlotCount = 0) => paidPickSlotCount * PICK_ADDON_FEE;

// 매장이 이번 달 플랫폼에 내야 하는 총액 = 수수료 + 플랜 구독료 + 매장관리자 기능
// 애드온 + PICK 추가 슬롯 요금. calcStoreFee 자체는 순수 수수료만 필요한 호출부를 위해
// 그대로 두고, 이 함수 안에서만 합산. paidPickSlotCount를 안 넘기면 0으로 취급(기존
// 호출부 하위 호환).
export const calcTotalOwed = (store, revenue, themeCount = 0, paidPickSlotCount = 0) =>
  calcStoreFee(store, revenue, themeCount) + getPlanFee(store) + getOpsAddonFee(store) + getPickAddonFee(paidPickSlotCount);

